<template>
    <div ref="videoContainer" class="video-player-hls">
        <video
            ref="videoElement"
            class="video-js"
        />
    </div>
</template>

<script setup lang="ts">
    import { eventsPlayer, unsupportedTextTrackIds, unsupportedTextTrackKinds } from './types';
    import { type ComponentInternalInstance } from "vue";
    import { VideoPlayerEvent, type IVideoPlayer } from "@/components/Player/types";
    import videojs from "video.js";
    import "video.js/dist/video-js.css";
    import type { PlayItemRestrictionsProcessor } from "@/components/Player/types/PlayItemRestrictionsProcessor";
    import { usePlayerStore } from "@/stores/Player";
    import type Player from 'video.js/dist/types/player';
    import type TextTrackDisplay from 'video.js/dist/types/tracks/text-track-display';
    import { isSamsung } from "@ca.vmedia.tv/domains";
    import { TizenOSVersion } from '@/plugins/platform/platform-tizen';
    import { AppConstants } from "@/common";
    import { useVideoPlayerErrorHandler } from '@/composables/player/useVideoPlayerErrorHandler';

    interface VideoJSPlayer extends Player {
        textTrackDisplay?: TextTrackDisplay;
    }

    const errorCategoriesToRetry: number[] = [
        // document: https://docs.videojs.com/mediaerror#.errorTypes
        MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED,
        MediaError.MEDIA_ERR_NETWORK,
        MediaError.MEDIA_ERR_DECODE,
    ];

    const PlayerStoreModule = usePlayerStore();
    const {
        saveSubtitles,
        saveAudio,
    } = PlayerStoreModule;

    const props = defineProps({
        customEventName: {
            type: String,
            default: "stateChanged"
        },
        subtitle: {
            type: String,
            default: "off"
        },
        audio: {
            type: String,
            default: ""
        },
        volume: {
            type: Number,
            default: 100
        },
        isPlayBeginning: {
            type: Boolean,
            default: false
        },
        itemProcessor: {
            type: [Object, null] as PropType<PlayItemRestrictionsProcessor | null>,
            required: true
        },
        preferredAudio: {
            type: String,
            default: ''
        },
        isAvailableDescriptiveAudio: {
            type: Boolean,
            required: true
        },
        isSeeking: {
            type: Boolean,
            required: true
        },
        mediaSource: {
            type: [String, null] as PropType<string | null>,
            required: true,
        },
    });

    const emit = defineEmits([
        ...eventsPlayer,
        'seeking',
        'seeked', 
        'timeupdate', 
        'ready',
        'set-subtitles', 
        'set-audio', 
        'retry', 
        'stateChanged', 
        'set-preferred-audio', 
        'hard-restart',
        'error',
        'error-507'
    ]);

    const { $platform } = useNuxtApp();

    const player: Ref<VideoJSPlayer | null> = ref(null);
    const currentSubtitle: Ref<string> = ref('');
    const currentAudio: Ref<string> = ref('');
    const videoElement: Ref<HTMLMediaElement | null> = ref(null);
    const videoContainer: Ref<HTMLDivElement | null> = ref(null);
    const instance: ComponentInternalInstance | null = getCurrentInstance();
    const httpErrorCode: Ref<number | null> = ref(null);

    const isOffline = computed((): boolean => !useOnline().value);
    const durationPreviousProgram: Ref<number> = ref(0);

    let liveDifferenceTime: number = 0;
    const setOffsetTimeForLive = () =>{
        if(props.itemProcessor?.isLive){
            liveDifferenceTime = player.value 
            ? Math.floor((TimeUtils.dateNow() / 1000) - ((player.value.currentTime() ?? 0) + props.itemProcessor?.startTimeSec)) 
            : 0;
        }
    }

    const videoOptions = {
        // document: https://docs.videojs.com/tutorial-options.html#fluid
        autoplay: true,
        controls: false,
        loop: false,
        language: 'en',
        preload: 'auto',
        sourceOrder: true,
        errorDisplay: false,
        fluid: false,
        liveui: true,
        loadingSpinner: false,
        html5: { 
            vhs: {
                overrideNative: true,
                maxPlaylistRetries: 5,
            },
            nativeAudioTracks: false,
            nativeVideoTracks: false,
            nativeTextTracks: false,
        },
        techOrder: ['html5'],
        plugins: {},
    }
    const switchOptionLoop = (url: string | null): void => {
        if (player.value) {
            player.value.loop(url?.includes(AppConstants.searchStringDemo) ?? false);
        }
    };

    const restart = () => {
        void nextTick(() => {
            const videoEl = videoElement.value;
            if (videoEl !== null) {
                videoEl.className = 'video-js'
                videoElement.value?.remove();
                videoContainer.value?.appendChild(videoEl);
                initialize();
            }
        });
    };

    const onLoadedMetadata = (): void => {
        if (props.isPlayBeginning && player.value) {
            player.value.currentTime(0);
        }
    };

    const onLoadedData = (): void => {
        initAudio();
        initSubtitles();
        emit("ready");
        setOffsetTimeForLive();
        
        // Setup fallback timer in case texttrackchange doesn't fire
        const subtitleTimeout = setTimeout(() => {
            setSubtitlesState();
        }, 5000);
        
        // Do not remove this listener, It`s required to show subtitles correctly
        player.value?.one("texttrackchange", () => {
            clearTimeout(subtitleTimeout);
            setSubtitlesState();
        });
    };

    const getCurrentTime = (): number  =>  {
        if (!player.value) {
            return 0;
        }

        let progress = 0;
        if (props.itemProcessor?.isLive) {
            progress = props.itemProcessor?.liveIsPausedOnce 
            ? (player.value.currentTime() ?? 0) + liveDifferenceTime - durationPreviousProgram.value 
            : (TimeUtils.dateNow() / 1000) - props.itemProcessor?.startTimeSec;
        } else {
            progress = (player.value.currentTime() ?? 0) - durationPreviousProgram.value;
        }
        
        return progress > 0 ? Math.floor(progress) : 0;
    };

    const mediaTime = ref(0);
    const lastMediaTime = ref(0);
    const _onTimeupdate = (): void => {
        if (props.isSeeking) {
            return;
        }

        mediaTime.value = getCurrentTime();
        if (lastMediaTime.value != mediaTime.value) {
            emit('timeupdate', mediaTime.value);
            lastMediaTime.value = mediaTime.value;
        }
    };

    const _onSeeking = (): void => {
        emit('seeking');
    };
    const _onkSeeked = (): void => {
        emit('seeked');
    };

    const errorHandler = (): void => {
        const error = player.value?.error();
        if (httpErrorCode.value !== null) {
            if (httpErrorCode.value == 507) {
                void dispose(() => {
                    emit("error-507");
                });
                return;
            }
            console.error('Error HTTP:', httpErrorCode.value)
        }
        if (error) {
            if (errorCategoriesToRetry.includes(error.code)) {
                void dispose(() => {
                    emit("retry");
                });
                return
            }
            void dispose(() => {
                emit('error', new VideoPlayerEvent("error", instance as unknown as IVideoPlayer, player.value, error));
            });
        }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const globalResponseHook = (_request: any, _error: any, response: { statusCode: any }) => {
        if (response.statusCode >= 400 && response.statusCode < 600) {
            httpErrorCode.value = response.statusCode;
            player.value?.trigger('error');
            return;
        }
    };

    const initialize = () => {
        // decreasing the buffer length for Tizen 6.0 to avoid playback issues
        if (isSamsung && $platform.getOSVersion() === TizenOSVersion.V6_0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const vhs = (videojs as any).Vhs;
            vhs.GOAL_BUFFER_LENGTH = 15; // 15 seconds, default is 30 seconds
            vhs.MAX_GOAL_BUFFER_LENGTH = 30; // 15 seconds, default is 60 seconds
            vhs.BACK_BUFFER_LENGTH = 15; // 15 seconds, default is 30 seconds
        }

        const _emitPlayerEvent = (eventName: string, eventObject: Event) => {
            if (eventName) {
                emit(eventName, new VideoPlayerEvent(eventName, instance as unknown as IVideoPlayer, player.value, eventObject));
            }
            if (eventObject) {
                emit(props.customEventName, new VideoPlayerEvent(eventName, instance as unknown as IVideoPlayer, player.value, eventObject));
            }
        };

        // player initialization
        player.value = videojs(videoElement.value as Element, videoOptions, function() {
            this.src({
                withCredentials: false,
                type: "application/x-mpegURL",
                src: props.mediaSource
            });
            switchOptionLoop(props.mediaSource);

            eventsPlayer.forEach((event) => {
                this.on(event, (evt: Event) => {
                    _emitPlayerEvent(event, evt);
                });
            });

            this.on('error', errorHandler);
            this.on('timeupdate', _onTimeupdate);
            this.on('seeking', _onSeeking);
            this.on('seeked', _onkSeeked);

            this.one("loadedmetadata", onLoadedMetadata);
            this.one("loadeddata", onLoadedData);
            setVolume(props.volume);
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((videojs as any).Vhs) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (videojs as any).Vhs?.xhr?.onResponse(globalResponseHook);
        }
    };

    const resume = (): Promise<void> | undefined  => player.value?.play();

    const setCurrentTime = (timeSec: number): void => {
        if(player.value) {
            player.value?.currentTime(timeSec + durationPreviousProgram.value);
        }
    };

    const setVolume = (volume: number): void => {
        if(player.value) {
            player.value?.volume(volume / 100); 
        }
    };

    const isPaused = (): boolean => !!player.value?.paused();

    const pause = (): void  => {
        if(player.value) {
            player.value?.pause();
        }
    };

    const dispose = async (callback?: () => void) => {
        if (player.value?.dispose) {
            pause();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (videojs as any).Vhs?.xhr?.offResponse(globalResponseHook);
            player.value.dispose();
            player.value = null;
            httpErrorCode.value = null;
            void nextTick(() => {
                callback?.();
            });
        }
    };

    const getTextTracks = (): TextTrack[] | undefined => {
        if (player.value) {
            const allTracks = player.value.textTracks?.();
            const tracks: TextTrack[] | undefined = allTracks?.tracks_.filter((item: TextTrack) => !unsupportedTextTrackKinds.includes(item.kind) && !unsupportedTextTrackIds.includes(item.id));
            return tracks;
        }
        return;
    };

    const initSubtitles = (): void => {
        const tracks = getTextTracks();
        emit('set-subtitles', tracks);
    };

    const setSubtitlesState = () => {
        if (!player.value) {
            return;
        }
        const tracks = getTextTracks();
        if (!tracks) {
            return;
        }

        if (props.subtitle && props.subtitle !== 'off') {
            handleSubtitleOn(tracks);
            //Do not remove this. This is needed for older browsers.
            try {
                player.value?.textTrackDisplay?.show();
            } finally {}
        } else {
            handleSubtitleOff(tracks);
            //Do not remove this. This is needed for older browsers.
            try {
                player.value?.textTrackDisplay?.hide();
            } finally {}
        }

        saveSubtitles(currentSubtitle.value);
    };

    const handleSubtitleOn = (tracks: TextTrack[]) => {
        let matchFound = false;
        
        // First pass: try to find and enable the preferred subtitle
        for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = 'disabled';
            if (tracks[i].id === props.subtitle) {
                tracks[i].mode = 'showing';
                currentSubtitle.value = tracks[i].id;
                matchFound = true;
            }
        }
        
        // Fallback: if preferred subtitle not found, enable first track
        if (!matchFound && tracks.length > 0) {
            tracks[0].mode = 'showing';
            currentSubtitle.value = tracks[0].id;
        }
    };

    const handleSubtitleOff = (tracks: TextTrack[]) => {
        for (const track of tracks) {
            track.mode = 'disabled';
        }
        currentSubtitle.value = 'off';
    };
    const initAudio = () => {
        if (player.value) {
            const audioTracks = player.value.audioTracks?.().tracks_;
            if (!audioTracks) {
                return;
            }
            emit('set-audio', audioTracks);
            for (let i = 0; i < audioTracks.length; i++) {
                audioTracks[i].enabled = false;
                if ((audioTracks[i].id === props.preferredAudio) && props.isAvailableDescriptiveAudio) {
                    audioTracks[i].enabled = true;
                    audioTracks[i].enabledChange_();
                    currentAudio.value = audioTracks[i].id;
                } else {
                    const mainTrack = audioTracks[0];
                    currentAudio.value = mainTrack.id;
                }
            }
            saveAudio(currentAudio.value);
        }
    };
    const setAudio = () => {
        const audioTracks = player.value?.audioTracks?.().tracks_;
        if (!audioTracks) {
            return;
        }
        const isPresentPreferredAudioTrack = audioTracks.find((track) => track.id === props.preferredAudio);
        for (let i = 0; i < audioTracks.length; i++) {
            audioTracks[i].enabled = false;
            if (audioTracks[i].id === props.audio) {
                audioTracks[i].enabled = true;
                audioTracks[i].enabledChange_();
                currentAudio.value = audioTracks[i].id;
            } else {
                const mainTrack = audioTracks.find((track) => track.kind.toLowerCase() === 'main');
                currentAudio.value = mainTrack.id;
            }
        }
        saveAudio(currentAudio.value);
        if (props.isAvailableDescriptiveAudio && (isPresentPreferredAudioTrack || !props.preferredAudio)) {
            emit("set-preferred-audio", currentAudio.value);
        }
    };
    const reInitTracks = () => {
        initSubtitles();
        initAudio();
    };

    watch(
        () => props.itemProcessor,
        (itemProcessorNew: PlayItemRestrictionsProcessor | null, itemProcessorOld: PlayItemRestrictionsProcessor | null) => {
            if(itemProcessorNew && itemProcessorOld && (itemProcessorNew!.playItemId === itemProcessorOld!.playItemId)) {
                return;
            }
            durationPreviousProgram.value = durationPreviousProgram.value + (itemProcessorOld?.lengthInS ?? 0);
            reInitTracks();
        }
    );

    watch(
        () => props.volume,
        (value: number) => {
            setVolume(value);
        }
    );
    watch(
        () => props.subtitle,
        () => {
            setSubtitlesState();
        }
    );
    watch(
        () => props.audio,
        () => {
            setAudio();
        }
    );
    watch(
        isOffline,
        (val: boolean) => {
            if (val) {
                void dispose();
            } else {
                emit("hard-restart");
            }
        }
    );

    useVideoPlayerErrorHandler(dispose, () => emit('retry'));

    onMounted(() => {
        if (!player.value && !!props.mediaSource) {
            initialize();
        }
    });

    onBeforeUnmount(() => {
        void dispose();
    });

    defineExpose<IVideoPlayer>({
        getCurrentTime,
        setCurrentTime,
        pause,
        resume,
        restart,
        dispose,
        isPaused
    });

</script>

<style lang="scss">
    @import url("styles.scss");
</style>